import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import sortBy from 'lodash/sortBy';

// Types
import { User, FeedbackRecognitionTemplateTypes } from 'Shared/constants/types';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Selectors
import { getFeedbackByTypeAndReceiverId, getFeedbackByTypeAndCreatorId } from 'Shared/selectors/Feedbacks.selectors';

// Constants
import QueryParamsConstants from 'Shared/constants/QueryParams.constants';

// Actions
import * as FeedbacksActions from 'Shared/actions/Feedbacks.actions';

// Components
import KudosContainer from 'Shared/components/KudosContainer/KudosContainer';
import WpButton from 'Wpds/components/button/WpButton';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import WpIcon from 'Wpds/components/icon/WpIcon';
import UserProfileEmptyState from './UserProfileEmptyState';

// Styles
import './UserProfileKudos.scss';
import { Illustrations } from 'Wpds/components/illustration';

enum ViewFilterType {
  receivedKudos = 'receivedKudos',
  createdKudos = 'createdKudos',
}

type Props = {
  user: User;
  showTitle?: boolean;
};

const UserProfileKudos = ({ user, showTitle = true }: Props) => {
  const dispatch = useDispatch();
  const [viewFilter, setViewFilter] = useState(ViewFilterType.receivedKudos);
  const [isLoadingReceiver, toggleIsLoadingReceiver] = useToggle(true);
  const [isLoadingCreator, toggleIsLoadingCreator] = useToggle(true);

  const { id } = user;

  useEffect(() => {
    const receiverParams = {
      [QueryParamsConstants.Api.LIMIT]: 5,
      [QueryParamsConstants.Api.OFFSET]: 0,
      [QueryParamsConstants.Api.RECEIVER_ID]: id,
      [QueryParamsConstants.Api.TEMPLATE_TYPE]: FeedbackRecognitionTemplateTypes.kudosDefault,
    };

    // Fetch kudos as receipient
    dispatch(FeedbacksActions.fetchFeedbacks(receiverParams)).then(() => {
      toggleIsLoadingReceiver();
    });

    const creatorParams = {
      [QueryParamsConstants.Api.LIMIT]: 5,
      [QueryParamsConstants.Api.OFFSET]: 0,
      [QueryParamsConstants.Api.CREATOR_ID]: id,
      [QueryParamsConstants.Api.TEMPLATE_TYPE]: FeedbackRecognitionTemplateTypes.kudosDefault,
    };

    // Fetch kudos as creator
    dispatch(FeedbacksActions.fetchFeedbacks(creatorParams)).then(() => {
      toggleIsLoadingCreator();
    });
  }, []);

  const receivedKudos = useSelector(state =>
    getFeedbackByTypeAndReceiverId(state, {
      templateType: FeedbackRecognitionTemplateTypes.kudosDefault,
      receiverId: id,
    })
  );

  const createdKudos = useSelector(state =>
    getFeedbackByTypeAndCreatorId(state, {
      templateType: FeedbackRecognitionTemplateTypes.kudosDefault,
      creatorId: id,
    })
  );

  const KudosMarkup = () => {
    if (isLoadingReceiver || isLoadingCreator) {
      return <>loading...</>;
    } else if (viewFilter === ViewFilterType.receivedKudos && receivedKudos.length === 0) {
      return (
        <UserProfileEmptyState
          title="There are no Kudos to see here"
          image={Illustrations.high_fiving_purple_bg}
          description="Any of their public Kudos will appear here."
        />
      );
    } else if (viewFilter === ViewFilterType.createdKudos && createdKudos.length === 0) {
      return (
        <UserProfileEmptyState
          title="There are no Kudos to see here"
          image={Illustrations.high_fiving_purple_bg}
          description="Any of public kudos they've sent will appear here."
        />
      );
    } else if (viewFilter === ViewFilterType.receivedKudos) {
      const kudos = sortBy(receivedKudos, ['createdAt']).reverse().slice(0, 5);
      return <KudosContainer kudos={kudos} />;
    } else if (viewFilter === ViewFilterType.createdKudos) {
      const kudos = sortBy(createdKudos, ['createdAt']).reverse().slice(0, 5);
      return <KudosContainer kudos={kudos} />;
    }

    return null;
  };

  const baseClassName = 'UserProfileKudos';

  return (
    <div className={baseClassName}>
      {showTitle && <h5 className={`${baseClassName}__title`}>Kudos</h5>}
      <div className={`${baseClassName}__utility-bar`}>
        <WpDropdown>
          <WpDropdownToggle>
            <WpButton variant="tertiary">
              {viewFilter === ViewFilterType.receivedKudos ? 'Received Kudos' : 'Sent Kudos'}
              <WpIcon name="angle-down" />
            </WpButton>
          </WpDropdownToggle>
          <WpDropdownMenu>
            <WpDropdownMenuItem onClick={() => setViewFilter(ViewFilterType.receivedKudos)}>
              Received Kudos
            </WpDropdownMenuItem>
            <WpDropdownMenuItem onClick={() => setViewFilter(ViewFilterType.createdKudos)}>
              Sent Kudos
            </WpDropdownMenuItem>
          </WpDropdownMenu>
        </WpDropdown>
      </div>
      <KudosMarkup />
    </div>
  );
};

export default UserProfileKudos;
